import { useCallback, useEffect } from 'react'
import { useAtom } from 'jotai'
import { Button } from '@highlight/ui'
import { useCmdEnterSave } from '@renderer/hooks/use-cmd-enter-save'
import type { StagedAction } from '../../../hooks/use-staged-actions'
import { requiresExplicitSave, type ActionInlineDraft } from '../inline-drafts'
import { useViewScope } from '../../../view-state-atoms'
import { ViewConfirmScrim } from '../../../components/view-confirm-scrim'
import { DraftSupersededWarning, SaveErrorBanner, useActionSaveSurface } from './save-surface'

/**
 * The leave guard's confirmation (companion to `useUnsavedChangesGuard`): open
 * while the scope's `pendingLeave` holds a deferred navigation, in the shared
 * `ViewConfirmScrim` at the view root.
 *
 * Save and Discard don't navigate themselves — they resolve the draft (through
 * the same `useActionSaveSurface` as the dirty footer), and the auto-resolve
 * effect below runs the deferred move once nothing owes a Save. One resolution
 * path means a footer save landing while the dialog is open (the floating bar
 * sits outside the scrim) leaves exactly the same way, and nothing can
 * navigate twice.
 */
export function UnsavedChangesDialog({
  action,
  draft,
  hasUnsavedChanges = requiresExplicitSave(draft),
}: {
  action: StagedAction
  draft: ActionInlineDraft | undefined
  hasUnsavedChanges?: boolean
}) {
  const [pendingLeave, setPendingLeave] = useAtom(useViewScope().pendingLeave)
  const { handleSave, handleDiscard, clearError, isBusy, errorMessage, isSuperseded } = useActionSaveSurface(
    action,
    draft,
  )
  const isOpen = pendingLeave !== null

  const handleCancel = useCallback(() => {
    clearError()
    setPendingLeave(null)
  }, [clearError, setPendingLeave])

  // Chat output can hold drafts for several actions, so navigation waits for all of them.
  useEffect(() => {
    if (!pendingLeave || hasUnsavedChanges) return
    pendingLeave()
    setPendingLeave(null)
  }, [pendingLeave, hasUnsavedChanges, setPendingLeave])

  // Escape cancels — capture phase with a full stop, or the sidepanel hotkey
  // handler's Escape would fire a second goBack.
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      handleCancel()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [isOpen, handleCancel])

  // The dialog suppresses the dirty footer, so it owns the save hotkey while
  // open.
  useCmdEnterSave(isOpen && !isBusy, handleSave)

  return (
    <ViewConfirmScrim
      isOpen={isOpen}
      onCancel={handleCancel}
      ariaLabel="Unsaved changes"
      className="corner-smooth max-w-[90%] space-y-3 rounded-xl p-3"
    >
      <p className="text-foreground my-0">Save changes to this action?</p>
      {errorMessage && <SaveErrorBanner message={errorMessage} />}
      {isSuperseded && <DraftSupersededWarning />}
      {/* Two answers only — staying is the scrim click or Escape. */}
      <div className="flex items-center gap-1.5">
        <Button variant="outline" shape="rounded" onClick={handleDiscard} disabled={isBusy}>
          Discard
        </Button>
        <Button
          onClick={() => void handleSave()}
          variant="primary"
          tone="success"
          shape="rounded"
          className="flex-1"
          disabled={isBusy}
          loading={isBusy}
          hotkey="cmd + enter"
        >
          Save changes
        </Button>
      </div>
    </ViewConfirmScrim>
  )
}
